import React from 'react';
import {useComponent} from '../component-library';
import {useFeatureAppId} from '../utils/use-feature-app-id';
import styled from 'styled-components';
import {TextVariant} from '../types';
import {generateTestId, TestIdElementType} from '../utils/test';

interface AnimatedNumberProps {
  value: string;
  inverted?: boolean;
}

interface SymbolProps extends AnimatedNumberProps {
  textVariant: TextVariant;
  testId?: string;
}

function Symbol({
  value,
  textVariant,
  testId,
  inverted,
}: SymbolProps): JSX.Element {
  const Text = useComponent('Text');
  const TextInverted = useComponent('TextInverted');

  const TextElement = inverted ? TextInverted : Text;

  return (
    <TextElement bold tag="p" variant={textVariant} testid={testId}>
      {value}
    </TextElement>
  );
}

function StripeDigits(props: {
  activeNumber: number;
  textVariant: TextVariant;
  inverted?: boolean;
}): JSX.Element {
  return (
    <StripeWrapper activeNumber={props.activeNumber}>
      {Array.from({length: 10}).map((_, index) => (
        <Symbol
          key={index}
          value={index.toString()}
          textVariant={props.textVariant}
          inverted={props.inverted}
        />
      ))}
    </StripeWrapper>
  );
}

function StripeSymbol(props: {
  symbol: string;
  textVariant: TextVariant;
  inverted?: boolean;
}): JSX.Element {
  return (
    <StripeWrapper activeNumber={0}>
      <Symbol
        value={props.symbol}
        textVariant={props.textVariant}
        inverted={props.inverted}
      />
    </StripeWrapper>
  );
}

const sanitizeValue = (value: string): string => {
  if (value.length > 1000) {
    throw new Error('Value is too large');
  }

  const removeHTMLTags = (input: string) =>
    input.replace(/<\/?[^>]+(>|$)/g, '');

  const MAX_INVALID_LENGTH = 3;

  // Matches and replaces any sequence of characters that are not
  // digits (0-9), whitespace (\s), letters from any alphabet (\p{L}), currency symbols (\p{Sc}),
  // or common punctuation marks (.,:').
  return removeHTMLTags(value).replace(
    /[^0-9\s\p{L}\p{Sc}.,:']+/gu,
    (match) => {
      return match.length > MAX_INVALID_LENGTH
        ? match.substring(0, MAX_INVALID_LENGTH)
        : match;
    }
  );
};

export function AnimatedNumber({
  value,
  inverted = true,
}: AnimatedNumberProps): JSX.Element {
  const sanitizedValue = sanitizeValue(value);

  // TODO: Remove when better solution is made for NGWD6-31601.
  const textVariant: TextVariant =
    sanitizedValue.length > 7 ? 'result-small' : 'result';

  const matchBefore = /^\D+/.exec(sanitizedValue);
  const unitBefore = matchBefore ? matchBefore[0] : '';

  const matchAfter = /\D+$/.exec(sanitizedValue);
  const unitAfter = matchAfter ? matchAfter[0] : '';

  const valueRaw = sanitizedValue
    .replace(unitBefore, '')
    .replace(unitAfter, '');
  const valueParts = valueRaw.split(/(\d|:|,|\.|\s|')/).filter((el) => el);

  const featureAppId = useFeatureAppId();

  if (unitBefore !== '') {
    valueParts.unshift(unitBefore);
  } else {
    valueParts.push(unitAfter);
  }

  return (
    <AnimatedNumberWrapper>
      <WidthPlaceholder>
        <Symbol
          value={sanitizedValue}
          textVariant={textVariant}
          testId={generateTestId({
            featureAppId,
            elementName: 'result-value',
            elementType: TestIdElementType.dynamic,
          })}
          inverted={inverted}
        />
      </WidthPlaceholder>
      <div aria-hidden>
        <Stripes>
          {valueParts.map((valuePart, index) => {
            // Different types of white spaces: No-Break Space (NBSP, 160, e.g. pl-PL market) and Narrow No-Break Space (NNBSP, 8239, e.g. fr-CH market)
            if (
              !isNaN(valuePart as any) &&
              valuePart !== String.fromCharCode(160) &&
              valuePart !== String.fromCharCode(8239)
            ) {
              return (
                <StripeDigits
                  key={index}
                  activeNumber={Number(valuePart)}
                  textVariant={textVariant}
                  inverted={inverted}
                />
              );
            } else {
              return (
                <StripeSymbol
                  key={index}
                  symbol={valuePart}
                  textVariant={textVariant}
                  inverted={inverted}
                />
              );
            }
          })}
        </Stripes>
      </div>
    </AnimatedNumberWrapper>
  );
}

const AnimatedNumberWrapper = styled.div`
  position: relative;
  width: max-content;
  overflow: hidden;
`;

const WidthPlaceholder = styled.div`
  visibility: hidden;
`;

const Stripes = styled.div`
  display: flex;
  position: absolute;
  top: 0;
`;

interface StripeWrapperProps {
  activeNumber: number;
}

const StripeWrapper = styled.div<StripeWrapperProps>`
  transform: translateY(${(props) => props.activeNumber * -10}%);
  transition: transform 1s;
  white-space: pre;
`;
